import { fetchV2 } from "../../lib/api-v2";

export type DocumentStatus = "extracting" | "ready" | "failed";

export interface KnowledgeDocument {
  doc_id: string;
  kb_id: string;
  title: string;
  filename: string;
  meta: Record<string, string>;
  status: DocumentStatus;
  error: string | null;
  page_count: number;
  pages?: string[];
  reviewed: boolean;
  updated_at: string;
}

export interface KnowledgeVersion {
  version: string;
  chunk_count: number;
  published_at: string;
}

export interface KnowledgeBase {
  kb_id: string;
  name: string;
  active_version: string | null;
  published_at: string | null;
  documents?: KnowledgeDocument[];
  versions?: KnowledgeVersion[];
}

/** Stored on the agent, so every version searches it; `null` detaches the KB. */
export interface AgentKnowledgeConfig {
  enabled: boolean;
  kb_id: string;
  top_k: number;
  filters: Record<string, string>;
}

const base = "/knowledge-bases";
const json = (body: unknown) => JSON.stringify(body);

export const listKnowledgeBases = () => fetchV2<KnowledgeBase[]>(base);

export const createKnowledgeBase = (name: string) =>
  fetchV2<KnowledgeBase>(base, { method: "POST", body: json({ name }) });

export const getKnowledgeBase = (kbId: string) => fetchV2<KnowledgeBase>(`${base}/${kbId}`);

export function uploadDocument(kbId: string, file: File, title: string, meta: Record<string, string>) {
  const form = new FormData();
  form.append("file", file);
  form.append("title", title);
  form.append("meta", JSON.stringify(meta));
  return fetchV2<KnowledgeDocument>(`${base}/${kbId}/documents`, { method: "POST", body: form });
}

export const getDocument = (kbId: string, docId: string) =>
  fetchV2<KnowledgeDocument>(`${base}/${kbId}/documents/${docId}`);

export const updateDocument = (
  kbId: string,
  docId: string,
  patch: Partial<Pick<KnowledgeDocument, "title" | "meta" | "pages" | "reviewed">>,
) => fetchV2<KnowledgeDocument>(`${base}/${kbId}/documents/${docId}`, { method: "PATCH", body: json(patch) });

export const deleteDocument = (kbId: string, docId: string) =>
  fetchV2<{ deleted: boolean }>(`${base}/${kbId}/documents/${docId}`, { method: "DELETE" });

export const setAgentKnowledge = (agentId: string, knowledge: AgentKnowledgeConfig | null) =>
  fetchV2<{ agent_id: string; knowledge: AgentKnowledgeConfig | null }>(
    `/agents/${encodeURIComponent(agentId)}/knowledge`,
    { method: "PUT", body: json({ knowledge }) },
  );

export const publishKnowledgeBase = (kbId: string) =>
  fetchV2<KnowledgeVersion>(`${base}/${kbId}/publish`, { method: "POST" });

/** "model: splendor-2023" lines <-> {model: "splendor-2023"}. */
export function textToPairs(text: string): Record<string, string> {
  const pairs: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const at = line.indexOf(":");
    if (at <= 0) continue;
    const key = line.slice(0, at).trim();
    const value = line.slice(at + 1).trim();
    if (key && value) pairs[key] = value;
  }
  return pairs;
}

export const pairsToText = (pairs: Record<string, string>) =>
  Object.entries(pairs).map(([key, value]) => `${key}: ${value}`).join("\n");
